import React, { useState } from 'react';
import styles from './PlaceListWidget.module.scss';

type PlaceItem = {
  place_id: string;
  name: string;
  types: string[];
  photos: string[];
  rating?: number;
  rating_count?: number;
  opening_hours?: string;
  address?: string;
  ctas?: ('view_details' | 'add_to_itinerary')[];
};

type PlaceListPayload = {
  title: string;
  items: PlaceItem[];
};

const GOOGLE_MAPS_KEY = (process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '').trim();

function normalizeGooglePhotoUrl(url?: string): string | undefined {
  if (!url) return undefined;
  const raw = url.startsWith('//') ? `https:${url}` : url;
  if (!raw.startsWith('http')) return undefined;

  // Places API media URLs often arrive without key/max size; make them browser-friendly.
  if (raw.includes('places.googleapis.com/v1/') && GOOGLE_MAPS_KEY && !raw.includes('key=')) {
    const separator = raw.includes('?') ? '&' : '?';
    return `${raw}${separator}key=${encodeURIComponent(GOOGLE_MAPS_KEY)}&maxHeightPx=360&maxWidthPx=640`;
  }
  return raw;
}

function fallbackStaticMap(address?: string, name?: string): string | undefined {
  if (!GOOGLE_MAPS_KEY) return undefined;
  // Prefer the place name for the map query; fall back to address only if name is empty.
  const query = encodeURIComponent(name || address || 'city center');
  return `https://maps.googleapis.com/maps/api/staticmap?center=${query}&zoom=12&size=640x360&scale=2&markers=color:red%7C${query}&key=${encodeURIComponent(GOOGLE_MAPS_KEY)}`;
}

function googleMapsLink(address?: string, name?: string): string | undefined {
  // Prefer the place name so the link opens on the right destination.
  const query = (name || address || '').trim();
  if (!query) return undefined;
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
}

function placeKind(types: string[]): 'airport' | 'city' | 'landmark' {
  if (types.some((type) => ['airport', 'airport_terminal'].includes(type))) return 'airport';
  if (types.some((type) => ['locality', 'political', 'city'].includes(type))) return 'city';
  return 'landmark';
}

/* Google Places `types` are classification codes ("locality", "political",
   "point_of_interest"), not labels. Only the ones that tell a traveller
   something get a tag; the rest are dropped. */
const TYPE_LABELS: Record<string, string> = {
  locality: 'City',
  city: 'City',
  postal_town: 'Town',
  administrative_area_level_1: 'Region',
  country: 'Country',
  airport: 'Airport',
  train_station: 'Train station',
  subway_station: 'Metro station',
  lodging: 'Hotel',
  restaurant: 'Restaurant',
  cafe: 'Café',
  bar: 'Bar',
  night_club: 'Nightlife',
  museum: 'Museum',
  art_gallery: 'Gallery',
  tourist_attraction: 'Attraction',
  historical_landmark: 'Landmark',
  church: 'Church',
  place_of_worship: 'Place of worship',
  park: 'Park',
  natural_feature: 'Nature',
  beach: 'Beach',
  zoo: 'Zoo',
  aquarium: 'Aquarium',
  amusement_park: 'Theme park',
  stadium: 'Stadium',
  shopping_mall: 'Shopping',
  spa: 'Spa',
};

function typeLabels(types: string[], limit: number): string[] {
  const labels = types.map((type) => TYPE_LABELS[type]).filter(Boolean);
  return Array.from(new Set(labels)).slice(0, limit);
}

/* The itinerary playbook sends this same title for every place result, the
   destination the traveller just named included -- where "start from"
   reads as the departure point. Swap it for one that fits what's shown. */
const GENERIC_TITLE = 'Places to start from';

function listTitle(title: string, items: PlaceItem[]): string {
  if (title !== GENERIC_TITLE) return title;
  if (items.length === 1 && placeKind(items[0].types || []) !== 'landmark') return 'Your destination';
  return 'Suggested places';
}

function MapPinIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 21s-7-6.2-7-11.5a7 7 0 0 1 14 0C19 14.8 12 21 12 21z" />
      <circle cx="12" cy="9.5" r="2.5" />
    </svg>
  );
}

export function PlaceListWidget({
  payload,
  onAddPlace,
}: {
  payload: PlaceListPayload;
  onAddPlace: (value: { label: string; id: string; kind: 'airport' | 'city' | 'landmark' }) => void;
}) {
  const singleItem = payload.items.length === 1;
  const [failedMedia, setFailedMedia] = useState<Record<string, boolean>>({});
  const title = payload.title ? listTitle(payload.title, payload.items) : '';

  return (
    <section className={styles.root} aria-label={title || 'Places'}>
      {title ? <h3 className={styles.title}>{title}</h3> : null}
      <div className={`${styles.grid} ${singleItem ? styles.single : ''}`}>
        {payload.items.map((item) => {
          const kind = placeKind(item.types || []);
          const tags = typeLabels(item.types || [], singleItem ? 4 : 2);
          const previewSrc = normalizeGooglePhotoUrl(item.photos?.[0]) || fallbackStaticMap(item.address, item.name);
          const mapsHref = googleMapsLink(item.address, item.name);
          const showImage = Boolean(previewSrc && !failedMedia[item.place_id]);
          return (
            <article key={item.place_id} className={styles.card}>
              <div className={styles.mediaWrap}>
                {showImage ? (
                  <img
                    src={previewSrc}
                    alt={item.name}
                    className={styles.media}
                    onError={() => setFailedMedia((current) => ({ ...current, [item.place_id]: true }))}
                  />
                ) : mapsHref ? (
                  // No photo and no static map (it needs an API key): a
                  // quiet map-style tile that links out, rather than a live
                  // Google Maps embed with all of its controls crammed into
                  // a thumbnail.
                  <a
                    className={styles.mapTile}
                    href={mapsHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`Open ${item.name} in Google Maps`}
                  >
                    <span className={styles.mapTilePin}>
                      <MapPinIcon className={styles.mapTileIcon} />
                    </span>
                    <span className={styles.mapTileLabel}>View on map</span>
                  </a>
                ) : <div className={styles.mediaPlaceholder} aria-hidden="true" />}
              </div>

              <div className={styles.content}>
                <h4 className={styles.name}>{item.name}</h4>
                {item.address ? <p className={styles.address}>{item.address}</p> : null}

                {tags.length > 0 && (
                  <div className={styles.tags}>
                    {tags.map((tag) => <span key={tag} className={styles.tag}>{tag}</span>)}
                  </div>
                )}

                {item.ctas?.includes('add_to_itinerary') ? (
                  <button
                    type="button"
                    className={styles.addButton}
                    onClick={() => onAddPlace({ label: item.name, id: item.place_id, kind })}
                  >
                    {/* The playbook makes whatever's picked here the trip's
                        destination region. */}
                    {kind === 'landmark' ? 'Add to itinerary' : 'Set as destination'}
                  </button>
                ) : null}
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
}

